/**
 * TestimonialRotator — reusable customer-voice band.
 *
 * Modes:
 *   • "rotator" (default) — one big quote at a time, auto-rotates every
 *     ~7s, with dot indicators and prev/next controls. Used on the
 *     homepage hero and service detail pages.
 *   • "grid"  — small 3-up grid (no rotation). Used when more density is
 *     desired.
 *   • "spotlight" — single hero card with the longest/highest-signal
 *     testimonial, no rotation. Used for highlighted features.
 *
 * Props:
 *   - lang: "en" | "ja"
 *   - mode: "rotator" | "grid" | "spotlight"
 *   - accent: brand hex (default purple)
 *   - softBg: pastel quote-box tint
 *   - eyebrowEn / eyebrowJa: small label above the band
 *   - headlineEn / headlineJa: large headline above the band
 *   - linkTo: optional "see all" path (e.g. `/${lang}/testimonials`)
 *   - take: how many to fetch (defaults sensibly per mode)
 *
 * Source: `useFeaturedTestimonials` hook — most recent, length-filtered.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ChevronLeft, ChevronRight, Quote, ArrowRight, ShieldCheck, MapPin, Building2 } from "lucide-react";

import Reveal from "@/components/motion/Reveal";
import PageSideAura from "@/components/motion/PageSideAura";
import useFeaturedTestimonials from "@/lib/useFeaturedTestimonials";
import { sanitizeField } from "@/lib/sanitizeTestimonial";
import SourceFreshnessBadge from "@/components/SourceFreshnessBadge";

const DATE_GREEN = "#2F8F3C";

function pickL(en, ja, lang) {
  return lang === "ja" ? ja : en;
}

function ratingToStars(rating) {
  // Owner-locked policy (2026-06-21): every approved testimonial
  // displays as 5 stars on the public site regardless of underlying
  // sub-ratings. Mirrors `ratingToStars` in `TestimonialsPage.jsx`.
  void rating;
  return 5;
}

function formatDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat(lang === "ja" ? "ja-JP" : "en-US", {
      year: "numeric",
      month: "short",
    }).format(d);
  } catch {
    return "";
  }
}

function Stars({ count = 5, size = 4 }) {
  const cls = `h-${size} w-${size}`;
  return (
    <div className="flex items-center gap-0.5" aria-label={`${count} of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cls}
          style={{ color: i < count ? "#F5B400" : "#E2E8F0" }}
          fill={i < count ? "#F5B400" : "transparent"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

export default function TestimonialRotator({
  lang = "en",
  mode = "rotator",
  accent = "#5B2E83",
  softBg = "#F4EEF9",
  eyebrowEn = "Customer voices",
  eyebrowJa = "お客様の声",
  headlineEn,
  headlineJa,
  linkTo,
  take,
  // When provided, restricts the rotator to a single sister-site's
  // reviews ("japanetccard" | "japantollreceipts" | "japanchangemoney").
  // Used by the service-detail-page spotlight + grid to prevent cross-
  // service contamination.
  serviceId = null,
  testIdSurface = "site",
}) {
  const isJa = lang === "ja";
  const effectiveTake =
    take ?? (mode === "spotlight" ? 1 : mode === "grid" ? 6 : 5);
  const { items, loading, sourceFreshness } = useFeaturedTestimonials({
    lang,
    take: effectiveTake,
    // Prefer longer, more substantive quotes for the spotlight surface.
    // Thresholds are language-aware: Japanese conveys the same content
    // in noticeably fewer characters than English (no spaces, denser
    // kanji), so a flat EN-tuned minLen silently starved JA spotlights
    // of any qualifying quote even when a perfectly good, complete
    // translation existed — this scales the bar down for JA rather
    // than dropping it, so short/low-effort quotes still get excluded.
    minLen: mode === "spotlight" ? (isJa ? 110 : 180) : (isJa ? 25 : 35),
    maxLen: mode === "spotlight" ? (isJa ? 500 : 800) : 320,
    serviceId,
  });

  const [idx, setIdx] = useState(0);

  // Auto-rotate (rotator mode only). 14 seconds — slow enough that the
  // visitor can read a full quote before it changes (owner directive).
  useEffect(() => {
    if (mode !== "rotator" || items.length <= 1) return undefined;
    const id = setInterval(() => {
      setIdx((p) => (p + 1) % items.length);
    }, 14000);
    return () => clearInterval(id);
  }, [mode, items.length]);

  // Clamp idx if list shrinks
  useEffect(() => {
    if (idx >= items.length) setIdx(0);
  }, [items.length, idx]);

  const headline =
    pickL(headlineEn, headlineJa, lang) ||
    pickL(
      "What our customers actually say.",
      "お客様の言葉が、いちばんの説明です。",
      lang
    );

  if (loading && items.length === 0) {
    return null; // Keep the page quiet until data is ready
  }
  if (!items || items.length === 0) return null;

  const current = items[Math.min(idx, items.length - 1)];

  return (
    <section
      className="relative isolate overflow-hidden"
      data-testid={`testimonial-rotator-${testIdSurface}`}
      style={{ backgroundColor: "#FFFFFF" }}
    >
      <PageSideAura testId={`testimonial-rotator-${testIdSurface}-aura`} />
      <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-8 md:mb-10">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 mb-3 flex-wrap">
              <span
                aria-hidden
                className="h-1.5 w-1.5"
                style={{ backgroundColor: accent }}
              />
              <span
                className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold"
                style={{ color: accent }}
              >
                {pickL(eyebrowEn, eyebrowJa, lang)}
              </span>
              <SourceFreshnessBadge
                sourceFreshness={sourceFreshness}
                lang={lang}
                className="ml-1"
              />
            </div>
            <h2
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] font-bold tracking-tighter leading-[1.1]",
              ].join(" ")}
            >
              {headline}
            </h2>
          </div>
          {linkTo && (
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to={linkTo}
                data-testid={`testimonial-rotator-${testIdSurface}-link`}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold tracking-tight hover:opacity-80"
                style={{ color: accent }}
              >
                {isJa ? "すべてのレビューを見る" : "See all reviews"}
                <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </Link>
            </div>
          )}
        </div>

        {/* Body */}
        {mode === "grid" ? (
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {items.map((it, i) => (
              <Reveal as="li" variant="up" delay={i * 70} key={it.id || i}>
                <div
                  className="h-full rounded-2xl bg-white ring-1 ring-[#E2E8F0] p-5 transition-all hover:-translate-y-0.5 hover:ring-transparent hover:shadow-[0_18px_40px_-20px_rgba(15,29,45,0.3)]"
                  data-testid={`testimonial-rotator-${testIdSurface}-card-${it.id || i}`}
                >
                  <Stars count={ratingToStars(it.rating)} size={4} />
                  <div
                    className="mt-3 rounded-xl px-4 py-3"
                    style={{ backgroundColor: softBg }}
                  >
                    <p className="text-[13.5px] leading-[1.7] text-[#0F1D2D] italic">
                      &ldquo;{it.summary}&rdquo;
                    </p>
                  </div>
                  <div className="mt-3 flex items-baseline justify-between">
                    <span
                      className="text-[14px] font-bold tracking-tight"
                      style={{ color: accent }}
                    >
                      {it.title || (isJa ? "お客様" : "Customer")}
                    </span>
                    <span
                      className="text-[11.5px] font-semibold tracking-tight"
                      style={{ color: DATE_GREEN }}
                    >
                      {formatDate(it.updatedAt, lang)}
                    </span>
                  </div>

                  {/* Location identity — branch · base · city · command.
                        Owner directive: "you need to put their locations
                        correctly with the city and everything." The full
                        Testimonials page renders the same trio; we
                        mirror that here so the 6-up grid carries the
                        same operational credibility. Each row hides
                        itself when the underlying field is empty. */}
                  {(() => {
                    /* Per-component sanitize each location field.
                       Owner directive: preserve readable values like
                       "Yokota Air Base" while suppressing only the
                       encrypted/empty/null components. This means
                       when `city` is `Enc:…` and `base` is a real
                       string, we still render the base and drop only
                       the city — no orphan " · " separator, no blank
                       row. Same shield applies to branch/command. */
                    const branch = sanitizeField(it.branch);
                    const base = sanitizeField(it.base);
                    const city = sanitizeField(it.city);
                    const command = sanitizeField(it.command);
                    if (!(branch || base || city || command)) return null;
                    return (
                      <ul
                        className="mt-3 pt-3 border-t border-[#E2E8F0] space-y-1.5 text-[11.5px] leading-[1.55] text-[#475569]"
                        data-testid={`testimonial-rotator-${testIdSurface}-location-${it.id || i}`}
                      >
                        {branch && (
                          <li className="flex items-start gap-1.5">
                            <ShieldCheck
                              className="h-3 w-3 mt-[1px] shrink-0"
                              strokeWidth={2.25}
                              style={{ color: accent }}
                            />
                            <span className="font-semibold text-[#0F1D2D]">{branch}</span>
                          </li>
                        )}
                        {(base || city) && (
                          <li className="flex items-start gap-1.5">
                            <MapPin
                              className="h-3 w-3 mt-[1px] shrink-0"
                              strokeWidth={2.25}
                              style={{ color: accent }}
                            />
                            <span>
                              {base}
                              {base && city && (
                                <span className="text-[#94A3B8]"> · </span>
                              )}
                              {city}
                            </span>
                          </li>
                        )}
                        {command && (
                          <li className="flex items-start gap-1.5">
                            <Building2
                              className="h-3 w-3 mt-[1px] shrink-0"
                              strokeWidth={2.25}
                              style={{ color: accent }}
                            />
                            <span>{command}</span>
                          </li>
                        )}
                      </ul>
                    );
                  })()}
                </div>
              </Reveal>
            ))}
          </ul>
        ) : (
          /* rotator + spotlight share the same single-card surface */
          <div
            className="relative rounded-3xl ring-1 ring-[#E2E8F0] bg-white overflow-hidden"
            data-testid={`testimonial-rotator-${testIdSurface}-hero`}
          >
            <div
              aria-hidden
              className="absolute top-0 left-0 right-0 h-1"
              style={{ backgroundColor: accent }}
            />
            <Quote
              aria-hidden
              className="absolute -top-3 right-6 h-24 w-24 opacity-[0.06]"
              style={{ color: accent }}
              strokeWidth={1}
            />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 p-6 md:p-10">
              <div className="md:col-span-8">
                <Stars count={ratingToStars(current.rating)} size={5} />
                <p
                  className={[
                    isJa ? "font-ja" : "font-display",
                    "mt-5 text-[#0F1D2D] text-[20px] md:text-[26px] lg:text-[30px] leading-[1.35] tracking-tight font-medium italic",
                  ].join(" ")}
                  data-testid={`testimonial-rotator-${testIdSurface}-quote`}
                >
                  &ldquo;{current.summary}&rdquo;
                </p>
                <div className="mt-6 flex items-center gap-4 flex-wrap">
                  <span
                    className="text-[16px] font-bold tracking-tight"
                    style={{ color: accent }}
                  >
                    {current.title || (isJa ? "お客様" : "Customer")}
                  </span>
                  <span className="text-[#CBD5E1]">·</span>
                  <span className="text-[12px] tracking-[0.16em] font-mono-display uppercase text-[#64748B]">
                    {/* Normalize legacy "JapanCardETC" service-name
                          coming from upstream — the public brand is
                          "JapanETCcard". */}
                    {current.serviceName === "JapanCardETC"
                      ? "JapanETCcard"
                      : current.serviceName || "JapanETCcard"}
                  </span>
                  {formatDate(current.updatedAt, lang) && (
                    <>
                      <span className="text-[#CBD5E1]">·</span>
                      <span
                        className="text-[12.5px] font-semibold tracking-tight"
                        style={{ color: DATE_GREEN }}
                      >
                        {formatDate(current.updatedAt, lang)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="md:col-span-4 flex flex-col items-stretch md:items-end justify-between gap-4 md:border-l md:border-[#F1F5F9] md:pl-8">
                <div
                  className="rounded-2xl px-5 py-4 w-full md:w-auto"
                  style={{ backgroundColor: softBg }}
                >
                  <div
                    className="text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold"
                    style={{ color: accent }}
                  >
                    {isJa ? "実際のお客様" : "Verified customer"}
                  </div>
                  <div className="mt-2 text-[14px] text-[#0F1D2D] leading-snug">
                    {isJa
                      ? "JapanETCcardで実際にカードをお持ちのお客様からの声です。"
                      : "Submitted directly by an active JapanETCcard user."}
                  </div>
                </div>
                {mode === "rotator" && items.length > 1 && (
                  <div className="flex items-center justify-between md:justify-end gap-3 self-stretch md:self-end">
                    <button
                      type="button"
                      aria-label={isJa ? "前へ" : "Previous"}
                      onClick={() =>
                        setIdx((p) => (p - 1 + items.length) % items.length)
                      }
                      data-testid={`testimonial-rotator-${testIdSurface}-prev`}
                      className="h-11 w-11 md:h-9 md:w-9 rounded-full ring-1 ring-[#E2E8F0] inline-flex items-center justify-center hover:ring-transparent hover:bg-[#F8F9FA] active:bg-[#F1F5F9] transition-colors"
                    >
                      <ChevronLeft className="h-5 w-5 md:h-4 md:w-4" style={{ color: accent }} strokeWidth={2.5} />
                    </button>
                    <div className="flex items-center gap-2 px-1">
                      {items.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          aria-label={`${isJa ? "レビュー" : "Review"} ${i + 1}`}
                          onClick={() => setIdx(i)}
                          data-testid={`testimonial-rotator-${testIdSurface}-dot-${i}`}
                          className={[
                            "h-2 md:h-1.5 rounded-full transition-all",
                            i === idx ? "w-8 md:w-7" : "w-2 md:w-1.5 hover:opacity-70",
                          ].join(" ")}
                          style={{
                            backgroundColor: i === idx ? accent : "#CBD5E1",
                          }}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      aria-label={isJa ? "次へ" : "Next"}
                      onClick={() => setIdx((p) => (p + 1) % items.length)}
                      data-testid={`testimonial-rotator-${testIdSurface}-next`}
                      className="h-11 w-11 md:h-10 md:w-10 rounded-full inline-flex items-center justify-center transition-all hover:scale-105 hover:shadow-[0_8px_20px_-8px_rgba(91,46,131,0.55)] active:scale-100"
                      style={{ backgroundColor: accent }}
                    >
                      <ChevronRight className="h-5 w-5 md:h-4 md:w-4 text-white" strokeWidth={2.75} />
                    </button>
                  </div>
                )}
              </div>
            </div>
            {mode === "spotlight" && linkTo && (
              <div
                className="relative border-t border-[#F1F5F9] px-6 md:px-10 py-4 md:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                data-testid={`testimonial-rotator-${testIdSurface}-footer`}
              >
                <span className="text-[12.5px] text-[#64748B] tracking-tight">
                  {isJa
                    ? "ここに掲載しているのは一例です。すべてのレビューを公開しています。"
                    : "This is just one story. Every customer review is publicly available."}
                </span>
                <Link
                  to={linkTo}
                  data-testid={`testimonial-rotator-${testIdSurface}-cta`}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12.5px] font-semibold tracking-tight transition-all hover:-translate-y-0.5 text-white whitespace-nowrap"
                  style={{ backgroundColor: accent }}
                >
                  {isJa ? "すべてのお客様の声を読む" : "Read all customer reviews"}
                  <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
